/**
 * src/pages/public/JoinInvite.jsx — an invited artist sets their password.
 *
 * No login, no account yet — the invite token in the URL is the only
 * credential, exactly like ConsentSign.jsx. Three terminal states to handle:
 * expired link, already-accepted link, and a fresh one ready to complete.
 *
 * REDESIGN.md Phase 4: restyled onto Stencil's tokens and components/ui/ —
 * classified as a portal page by audit.md (public, token-gated, same pattern
 * as the other three) even though its audience is an artist, not a client.
 * No change to what this page sends or when.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../lib/auth.jsx';
import { useApi } from '../../lib/useApi.js';
import { ErrorBanner, Loader } from '../../components/ui.jsx';
import { Button, Input, PortalHeader } from '../../components/ui/index.js';
import PortalFooter from './PortalFooter.jsx';
import './portal.css';

export default function JoinInvite() {
  const { token } = useParams();
  const { data, error, loading } = useApi(`/api/invites/${token}`);
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await acceptInvite(token, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-page" style={{ maxWidth: 460 }}>
      <main className="portal-main">
        <Loader loading={loading} error={error}>
          {data ? (
            <>
              <PortalHeader studioName={data.studioName} />
              <p className="portal-lead" style={{ textAlign: 'center', marginTop: -16, marginBottom: 20 }}>
                Join as {data.artistName}
              </p>

              {data.status === 'expired' ? (
                <ExpiredNotice />
              ) : data.status === 'accepted' ? (
                <AlreadyJoinedNotice />
              ) : (
                <section className="portal-panel portal-panel--current">
                  <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                  <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                  <form className="stack" onSubmit={submit}>
                    <p style={{ marginTop: 0 }}>
                      Set a password for <strong>{data.email}</strong> to finish joining {data.studioName}.
                    </p>

                    <Input
                      id="password"
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <Input
                      id="confirm"
                      label="Confirm password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />

                    <ErrorBanner>{submitError}</ErrorBanner>

                    <Button type="submit" variant="primary" disabled={submitting}>
                      {submitting ? 'Joining…' : 'Set password and join'}
                    </Button>
                  </form>
                </section>
              )}
            </>
          ) : null}
        </Loader>
      </main>
      <PortalFooter />
    </div>
  );
}

/** Terminal state: link expired. */
function ExpiredNotice() {
  return (
    <div className="portal-panel" style={{ textAlign: 'center' }}>
      <h2 className="portal-panel-title">This invite has expired</h2>
      <p>Ask the studio owner to resend your invite from Settings.</p>
    </div>
  );
}

/** Terminal state: already accepted. */
function AlreadyJoinedNotice() {
  return (
    <div className="portal-panel" style={{ textAlign: 'center' }}>
      <h2 className="portal-panel-title">You&rsquo;ve already joined</h2>
      <p>This invite has already been used. Sign in with the password you set.</p>
      <p>
        <a className="btn btn-primary" href="/login">
          Go to sign in
        </a>
      </p>
    </div>
  );
}
